import Phaser from 'phaser';
import {
  PlayerPosition,
  HakemCardRevealedMessage,
  HakemSelectedMessage,
  PartnerSelectedMessage,
  SeatsSwappedMessage,
  AnimationEvent,
  AnimationSequenceId,
  GAME_WIDTH,
  GAME_HEIGHT,
} from '@hokm/shared';
import { PERSIAN_THEME } from '../../../theme/persianTheme';
import { RENDER_SCALE } from '../../../config';
import { PlayerPositionConfig } from '../../../config/positions';

export interface PlayerLabelContainer {
  bg: Phaser.GameObjects.Rectangle;
  hakemBanner?: Phaser.GameObjects.Container;
}

export interface CeremonyAnimatorCallbacks {
  getPositionConfig: () => Record<PlayerPosition, PlayerPositionConfig>;
  toDisplayPosition: (serverPosition: PlayerPosition) => PlayerPosition;
  sendAnimationAck: (event: AnimationEvent, sequenceId: AnimationSequenceId) => void;
  getPlayerLabelContainer: (position: PlayerPosition) => PlayerLabelContainer | undefined;
  getCurrentHakemPosition: () => PlayerPosition | null;
}

/**
 * Handles ceremony animations: hakem card reveal, hakem selection, partner selection, seat swaps.
 */
export class CeremonyAnimator {
  private scene: Phaser.Scene;
  private callbacks: CeremonyAnimatorCallbacks;

  constructor(scene: Phaser.Scene, callbacks: CeremonyAnimatorCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
  }

  /**
   * Animate a card being revealed during hakem selection ritual.
   */
  animateHakemCardReveal(data: HakemCardRevealedMessage): void {
    const displayPosition = this.callbacks.toDisplayPosition(data.recipientPosition);
    const target = this.callbacks.getPositionConfig()[displayPosition].hakemReveal;
    const textureKey = `card-${data.card.suit}-${data.card.rank}`;

    const cardImg = this.scene.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 30, textureKey);
    cardImg.setScale(0.3 / RENDER_SCALE);
    cardImg.setAlpha(0);
    cardImg.setDepth(50);

    // Safety cleanup
    const maxLifetime = data.isAce ? 1650 : 750;
    this.scene.time.delayedCall(maxLifetime, () => {
      if (cardImg?.active) cardImg.destroy();
    });

    this.scene.tweens.add({
      targets: cardImg,
      x: target.x,
      y: target.y,
      rotation: target.rotation,
      scale: 0.6 / RENDER_SCALE,
      alpha: 1,
      duration: 350,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        this.callbacks.sendAnimationAck(AnimationEvent.HAKEM_CARD_REVEAL, data.sequenceId);

        if (data.isAce) {
          const glow = this.scene.add.ellipse(target.x, target.y, 80, 100, PERSIAN_THEME.colors.gold, 0.5);
          glow.setDepth(49);

          this.scene.tweens.add({
            targets: glow,
            scale: 1.3,
            alpha: 0.8,
            duration: 300,
            yoyo: true,
            repeat: 1,
            onComplete: () => {
              if (glow?.active) glow.destroy();
              if (cardImg?.active) cardImg.destroy();
            },
          });
        } else {
          this.scene.tweens.add({
            targets: cardImg,
            alpha: 0,
            scale: 0.4,
            duration: 200,
            delay: 100,
            onComplete: () => {
              if (cardImg?.active) cardImg.destroy();
            },
          });
        }
      },
    });
  }

  /**
   * Show announcement when hakem is selected.
   */
  showHakemSelected(data: HakemSelectedMessage): Promise<void> {
    return new Promise((resolve) => {
      const displayPosition = this.callbacks.toDisplayPosition(data.hakemPosition);
      const labelContainer = this.callbacks.getPlayerLabelContainer(displayPosition);
      const labelCoord = this.callbacks.getPositionConfig()[displayPosition].label;

      // Pulse hakem banner
      if (labelContainer?.hakemBanner) {
        this.scene.tweens.add({
          targets: labelContainer.hakemBanner,
          scaleX: 1.15,
          scaleY: 1.15,
          duration: 200,
          yoyo: true,
          repeat: 2,
          ease: 'Sine.easeInOut',
        });
      }

      // Particle burst
      const key = 'gold-dust-particle';
      if (this.scene.textures.exists(key)) {
        const emitter = this.scene.add.particles(labelCoord.x, labelCoord.y, key, {
          speed: { min: 50, max: 120 },
          scale: { start: 0.5 / RENDER_SCALE, end: 0.1 / RENDER_SCALE },
          alpha: { start: 0.9, end: 0 },
          lifespan: 800,
          blendMode: Phaser.BlendModes.ADD,
          tint: PERSIAN_THEME.colors.gold,
          emitting: false,
        });
        emitter.setDepth(151);
        emitter.explode(20);
        this.scene.time.delayedCall(1000, () => emitter.destroy());
      }

      this.scene.time.delayedCall(1000, () => {
        this.callbacks.sendAnimationAck(AnimationEvent.HAKEM_SELECTED, data.sequenceId);
        resolve();
      });
    });
  }

  /**
   * Show announcement when partner is selected.
   */
  showPartnerSelected(data: PartnerSelectedMessage): Promise<void> {
    return new Promise((resolve) => {
      const partnerDisplayPos = this.callbacks.toDisplayPosition(data.partnerPosition);
      const hakemDisplayPos = this.callbacks.getCurrentHakemPosition() ?? PlayerPosition.SOUTH;
      const partnerCoord = this.callbacks.getPositionConfig()[partnerDisplayPos].label;

      const partnerContainer = this.callbacks.getPlayerLabelContainer(partnerDisplayPos);
      const hakemContainer = this.callbacks.getPlayerLabelContainer(hakemDisplayPos);

      [partnerContainer, hakemContainer].forEach((container) => {
        if (container) {
          this.scene.tweens.add({
            targets: container.bg,
            scaleX: 1.1,
            scaleY: 1.1,
            duration: 200,
            yoyo: true,
            repeat: 2,
            ease: 'Sine.easeInOut',
          });
        }
      });

      const key = 'gold-dust-particle';
      if (this.scene.textures.exists(key)) {
        const emitter = this.scene.add.particles(partnerCoord.x, partnerCoord.y, key, {
          speed: { min: 40, max: 100 },
          scale: { start: 0.4 / RENDER_SCALE, end: 0.1 / RENDER_SCALE },
          alpha: { start: 0.8, end: 0 },
          lifespan: 700,
          blendMode: Phaser.BlendModes.ADD,
          tint: PERSIAN_THEME.colors.teamA,
          emitting: false,
        });
        emitter.setDepth(151);
        emitter.explode(15);
        this.scene.time.delayedCall(1000, () => emitter.destroy());
      }

      this.scene.time.delayedCall(1200, () => {
        this.callbacks.sendAnimationAck(AnimationEvent.PARTNER_SELECTED, data.sequenceId);
        resolve();
      });
    });
  }

  /**
   * Show animation when players swap seats.
   */
  showSeatsSwapped(data: SeatsSwappedMessage): Promise<void> {
    return new Promise((resolve) => {
      const displayPos1 = this.callbacks.toDisplayPosition(data.swaps[0].toPosition);
      const displayPos2 = this.callbacks.toDisplayPosition(data.swaps[1].toPosition);

      const container1 = this.callbacks.getPlayerLabelContainer(displayPos1);
      const container2 = this.callbacks.getPlayerLabelContainer(displayPos2);

      [container1, container2].forEach((container) => {
        if (container) {
          this.scene.tweens.add({
            targets: container.bg,
            scaleX: 1.1,
            scaleY: 1.1,
            duration: 200,
            yoyo: true,
            repeat: 1,
            ease: 'Sine.easeInOut',
          });
        }
      });

      this.scene.time.delayedCall(800, () => {
        this.callbacks.sendAnimationAck(AnimationEvent.SEATS_SWAPPED, data.sequenceId);
        resolve();
      });
    });
  }

  destroy(): void {
    // No persistent state to clean up
  }
}
